import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { getProductBySlug } from '../data/products'
import SEO from '../components/seo/SEO'
import { MotionButton, MotionCard, MotionImage, MotionSection } from '../components/motion/Motion'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>()
  const product = slug ? getProductBySlug(slug) : null

  if (!product) {
    return (
      <MotionSection className="w-[96%] mx-auto py-12 lg:py-16 min-h-screen">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist.</p>
          <Link
            to="/products"
            className="inline-block rounded-full bg-pink-500 px-6 py-2 text-sm font-semibold text-white shadow hover:bg-pink-600 transition-colors"
          >
            <MotionButton>View All Products</MotionButton>
          </Link>
        </div>
      </MotionSection>
    )
  }

  const productUrl = `https://www.logobrandeddoormats.co.za/product/${product.slug}`
  const productImageUrl = `https://www.logobrandeddoormats.co.za${encodeURI(product.image)}`

  const handleWhatsAppQuote = () => {
    const phoneNumber = '27810634772'
    const message = encodeURIComponent(`Hello! I would like to request a quote for ${product.title}.`)
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank')
  }

  return (
    <>
      <SEO
        title={`${product.title} | Logo Branded Door Mats Johannesburg – Buy Now`}
        description={product.description}
        keywords={product.keywords}
        url={productUrl}
        image={productImageUrl}
        type="product"
      />
      
      {/* Structured Data for Product */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product.title,
            description: product.description,
            image: productImageUrl,
            brand: {
              '@type': 'Brand',
              name: 'Logo Branded Door Mats Johannesburg',
            },
            offers: {
              '@type': 'Offer',
              url: productUrl,
              priceCurrency: 'ZAR',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Organization',
                name: 'Logo Branded Door Mats Johannesburg',
              },
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.8',
              reviewCount: '150',
            },
          }),
        }}
      />

      <MotionSection className="w-[96%] mx-auto py-8 lg:py-12">
        <div className="mb-4">
          <Link
            to="/products"
            className="text-sm text-gray-600 hover:text-pink-500 transition-colors"
          >
            ← Back to Products
          </Link>
        </div>

        <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
          {/* Product Image */}
          <MotionImage className="w-full" delay={0.02}>
            <div className="bg-gray-50 rounded-lg p-4 lg:p-8">
              <img
                src={encodeURI(product.image)}
                alt={product.title}
                className="w-full h-auto object-contain max-h-[600px] mx-auto"
              />
            </div>
          </MotionImage>

          {/* Product Details */}
          <MotionCard className="space-y-6" delay={0.05}>
            <div>
              <span className="text-sm font-semibold text-pink-500 uppercase tracking-wide">
                {product.category}
              </span>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2">
                {product.title}
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex text-yellow-400">
                {'★'.repeat(5)}
              </div>
              <span className="text-sm text-gray-600">(150+ reviews)</span>
            </div>

            <div>
              <p className="text-lg text-gray-700 leading-relaxed">
                {product.longDescription}
              </p>
            </div>

            {product.priceRange && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Price (ZAR Incl. VAT)</p>
                <p className="text-2xl font-bold text-pink-500">{product.priceRange}</p>
              </div>
            )}

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
              <ul className="space-y-2">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-pink-500 mt-1">✓</span>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Specifications */}
            {product.specifications && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Specifications</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  {product.specifications.material && (
                    <p><span className="font-semibold">Material:</span> {product.specifications.material}</p>
                  )}
                  {product.specifications.sizes && (
                    <p><span className="font-semibold">Sizes:</span> {Array.isArray(product.specifications.sizes) ? product.specifications.sizes.join(', ') : product.specifications.sizes}</p>
                  )}
                  {product.specifications.durability && (
                    <p><span className="font-semibold">Durability:</span> {product.specifications.durability}</p>
                  )}
                </div>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={handleWhatsAppQuote}
                className="flex-1 flex items-center justify-center gap-2 rounded-full bg-green-500 px-6 py-3 text-sm font-semibold text-white shadow hover:bg-green-600 transition-colors"
              >
                <WhatsAppIcon fontSize="small" />
                <MotionButton>Request Quote on WhatsApp</MotionButton>
              </button>
              <Link
                to="/contact"
                className="flex-1 flex items-center justify-center rounded-full bg-pink-500 px-6 py-3 text-sm font-semibold text-white shadow hover:bg-pink-600 transition-colors"
              >
                <MotionButton>Contact Us</MotionButton>
              </Link>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">Available:</span> In Stock | 2–3 Day Delivery Across Gauteng
              </p>
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-semibold">Custom Branding:</span> Available for all products
              </p>
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-semibold">Payment:</span> Secure ZAR EFT or credit card
              </p>
            </div>
          </MotionCard>
        </div>
      </MotionSection>
    </>
  )
}
